import { IconArrowSquareOut } from "@btnet/blueprint-icons";
import { BTLink } from "@btnet/core-components/BTLink";
import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";
import { BTText } from "@btnet/core-components/BTText";
import type { WhatsNewItemResponse } from "@btnet/services-btnet/handlers/models/whatsNewItemResponse";
import { cn } from "@btnet/utilities-core";

import { DateDisplayDateOrRecentLabel } from "commonComponents/utilities/DateDisplay/DateDisplayDate";

import {
    defaultCtaLabel,
    getWhatsNewAreaLabel,
    isInternalUrl,
} from "entity/whatsNew/whatsNew.utils";

interface IWhatsNewCardProps {
    className?: string;
    item: WhatsNewItemResponse;
    isUnread: boolean;
    onCtaClicked: (item: WhatsNewItemResponse) => void;
}

export function WhatsNewCard({
    className,
    item,
    isUnread,
    onCtaClicked,
}: Readonly<IWhatsNewCardProps>) {
    const { ctaUrl } = item;

    return (
        <div
            className={cn("WhatsNewCard flex flex-col gap-2.5", className)}
            data-testid="whatsNewCard"
        >
            <div className="flex min-h-8 items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    {/* Kept when read, so the badge stays in line with the text below. */}
                    <div className="size-2.5 shrink-0">
                        {isUnread && (
                            <>
                                <div
                                    className="size-full rounded-full bg-blue-70"
                                    data-testid="whatsNewCardUnreadDot"
                                />
                                <span className="sr-only">New</span>
                            </>
                        )}
                    </div>
                    <BTStatusBadge
                        testid="whatsNewCardArea"
                        text={getWhatsNewAreaLabel(item.area)}
                        displayType="default"
                        textOnly
                    />
                </div>
                <BTText size="normal-sm" className="shrink-0 text-fg-secondary">
                    <DateDisplayDateOrRecentLabel
                        value={item.publishedDateUtc}
                        showTime={false}
                        showDayOfWeek={false}
                        data-testid="whatsNewCardPublishedDate"
                    />
                </BTText>
            </div>
            <div className="flex flex-col gap-2 pl-4.5">
                <div className="flex flex-col">
                    <BTText
                        // Bold, like notification titles.
                        className="font-bold"
                        testid="whatsNewCardTitle"
                        text={item.title}
                        size="distinct-sm"
                    />
                    <BTText
                        // Plain text, so keep the author's line breaks.
                        className="whitespace-pre-wrap"
                        testid="whatsNewCardBody"
                        text={item.body}
                        size="normal-md"
                    />
                </div>
                {ctaUrl && (
                    <BTLink
                        className="inline-flex items-center gap-1.5 self-start"
                        data-testid="whatsNewCardCta"
                        href={ctaUrl}
                        shouldOpenNewTab
                        onClick={() => {
                            onCtaClicked(item);
                        }}
                    >
                        {!isInternalUrl(ctaUrl) && (
                            // Icons default to near-black; match the link.
                            <IconArrowSquareOut
                                testid="whatsNewCardCtaExternalIcon"
                                className="text-fg-link"
                            />
                        )}
                        {item.ctaLabel || defaultCtaLabel}
                    </BTLink>
                )}
            </div>
        </div>
    );
}
